import { useBuildingSetupDraft } from "@/domains/billing/useBuildingSetupDraft";
import { Loader2 } from "lucide-react";
import BuildingIcon from "lucide-react/dist/esm/icons/building.js";
import type { Control, UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { sanitizeString } from "@/utils/sanitization";
import useBuildingForm, { type BuildingFormValues } from "@/hooks/useBuildingForm";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import {
  buildingRegistryFromAddress,
  formatAdsAddress,
  type AdsAddressResult,
} from "@/hooks/useAddressSearch";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useBuildingCreationPlan } from "@/domains/billing/useBuildingCreationPlan";
import { BuildingPlanChoice } from "@/domains/billing/components/BuildingPlanChoice";
import { BuildingPlanRestriction } from "@/domains/billing/components/BuildingPlanRestriction";
import { BuildingCostQuote } from "@/domains/billing/components/BuildingCostConsentDialog";
import {
  BuildingCreationDialogContent,
  BuildingCreationDialogShell,
} from "./BuildingCreationDialogContent";
import {
  hasLegacyBillingAccess,
  useBillingStatus,
  type BillingStatusContextValue,
} from "@/domains/billing/BillingStatusContext";

interface BuildingFormProps {
  /** Render for a modal host: drop the outer Card chrome (the dialog supplies
   * the title), show a Cancel/Create footer, and render immediately instead of
   * waiting to scroll into view. */
  isModal?: boolean;
  /** Modal-only: close the hosting dialog when the user cancels. */
  onCancel?: () => void;
  /** Modal-only: called after a building is created, before navigation. */
  onSuccess?: () => void;
}

function BuildingNameField({
  control,
  disabled,
}: {
  control: Control<BuildingFormValues>;
  disabled: boolean;
}) {
  const { t } = useLanguage();
  return (
    <FormField
      control={control}
      name="name"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {t.buildings.form.name} <span className="text-red-500">*</span>
          </FormLabel>
          <FormControl>
            <Input
              placeholder={t.buildings.form.name}
              {...field}
              className="rounded-xl"
              onChange={(e) => field.onChange(sanitizeString(e.target.value))}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function BuildingAddressField({
  form,
  disabled,
}: {
  form: UseFormReturn<BuildingFormValues>;
  disabled: boolean;
}) {
  const { t } = useLanguage();
  return (
    <FormField
      control={form.control}
      name="address"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {t.buildings.form.address} <span className="text-red-500">*</span>
          </FormLabel>
          <FormControl>
            <AddressAutocomplete
              value={field.value}
              onChange={field.onChange}
              onSelect={(result: AdsAddressResult) => {
                field.onChange(sanitizeString(formatAdsAddress(result)));
                const registry = buildingRegistryFromAddress(result);
                if (registry)
                  form.setValue("registry_code", registry, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
              }}
              placeholder={t.buildings.form.address}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function BuildingRegistryField({
  control,
  disabled,
}: {
  control: Control<BuildingFormValues>;
  disabled: boolean;
}) {
  const { t } = useLanguage();
  return (
    <FormField
      control={control}
      name="registry_code"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t.buildings.registryCode}</FormLabel>
          <FormControl>
            <Input
              placeholder={t.buildings.registryCode}
              {...field}
              className="rounded-xl"
              maxLength={9}
              onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 9))}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function BuildingFormActions({
  isModal,
  isSubmitting,
  disabled,
  onCancel,
  agreesPlan,
}: {
  isModal: boolean;
  isSubmitting: boolean;
  disabled: boolean;
  onCancel?: () => void;
  agreesPlan: boolean;
}) {
  const { t } = useLanguage();
  const label = isSubmitting ? (
    <>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      {t.buildings.creatingBuilding}
    </>
  ) : agreesPlan ? (
    t.billing.consent.agreeAdd
  ) : (
    t.buildings.form.create
  );

  if (isModal) {
    return (
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t.common.cancel}
        </Button>
        <Button type="submit" disabled={disabled}>
          {label}
        </Button>
      </div>
    );
  }

  return (
    <Button type="submit" className="w-full rounded-full" disabled={disabled}>
      {label}
    </Button>
  );
}

type CreationPlanSelection = ReturnType<typeof useBuildingCreationPlan> & { onSetup?: () => void };

function BuildingFormPlan({
  selection,
  disabled,
}: {
  selection: CreationPlanSelection;
  disabled: boolean;
}) {
  const { t } = useLanguage();
  return (
    <>
      {selection.plans && (
        <BuildingPlanChoice
          plans={selection.plans}
          tier={selection.tier}
          onChange={selection.setTier}
          disabled={disabled}
        />
      )}
      {selection.isLoading && (
        <p role="status" className="text-sm text-muted-foreground">
          {t.billing.consent.loading}
        </p>
      )}
      {selection.unavailable && (
        <div className="space-y-2">
          <p role="status" className="text-sm text-destructive">
            {t.billing.consent.quoteUnavailable}
          </p>
          <Button type="button" variant="outline" onClick={selection.retry} disabled={disabled}>
            {t.billing.consent.retry}
          </Button>
        </div>
      )}
      {selection.quote && selection.currency && (
        <BuildingCostQuote quote={selection.quote} currency={selection.currency} />
      )}
      <BuildingPlanRestriction
        reason={selection.reason}
        needsBillingSetup={selection.needsBillingSetup}
        onSetup={selection.onSetup}
      />
    </>
  );
}

type BuildingFormState = Omit<ReturnType<typeof useBuildingForm>, "ref">;

interface BuildingFormViewProps {
  state: BuildingFormState;
  selection?: CreationPlanSelection;
  disabled: boolean;
  onCancel?: () => void;
}

function BuildingFormModal({ state, selection, disabled, onCancel }: BuildingFormViewProps) {
  const {
    form,
    onSubmit,
    isSubmitting,
    createBlockedReason,
    currentOrganization,
    t,
    costConsentDialog,
  } = state;
  return (
    <>
      <BuildingCreationDialogContent
        form={form}
        onSubmit={onSubmit}
        onCancel={onCancel}
        disabled={disabled}
        submitting={isSubmitting}
        submitDisabled={disabled || Boolean(selection && !selection.consent.canSubmit)}
        selection={selection}
        reason={
          createBlockedReason ??
          (!currentOrganization ? t.buildings.selectOrgBeforeCreating : undefined)
        }
      />
      {!selection && costConsentDialog}
    </>
  );
}

function BuildingFormCard({ state, selection, disabled, onCancel }: BuildingFormViewProps) {
  const {
    form,
    onSubmit,
    isSubmitting,
    createBlockedReason,
    currentOrganization,
    t,
    costConsentDialog,
  } = state;
  const formBody = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {!currentOrganization && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">{t.buildings.selectOrgBeforeCreating}</p>
          </div>
        )}
        {createBlockedReason && (
          <p role="status" className="text-sm text-muted-foreground">
            {createBlockedReason}
          </p>
        )}

        <BuildingNameField control={form.control} disabled={disabled} />
        <BuildingAddressField form={form} disabled={disabled} />
        <BuildingRegistryField control={form.control} disabled={disabled} />
        {selection && <BuildingFormPlan selection={selection} disabled={disabled} />}

        <BuildingFormActions
          isModal={false}
          isSubmitting={isSubmitting}
          disabled={disabled || Boolean(selection && !selection.consent.canSubmit)}
          onCancel={onCancel}
          agreesPlan={Boolean(selection)}
        />
      </form>
      {!selection && costConsentDialog}
    </Form>
  );

  return (
    <Card className="mx-auto w-full max-w-2xl rounded-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BuildingIcon className="h-6 w-6" />
          {t.buildings.addNew}
        </CardTitle>
      </CardHeader>
      <CardContent>{formBody}</CardContent>
    </Card>
  );
}

function useBuildingDraftSelection(state: BuildingFormState, selection?: CreationPlanSelection) {
  const orgId = state.currentOrganization ? state.currentOrganization.id : "";
  const { tier = "free", setTier } = selection ?? {};
  const draft = useBuildingSetupDraft(orgId, state.form, tier, setTier);
  return {
    selection: selection ? { ...selection, onSetup: draft.startSetup } : undefined,
    clearDraft: draft.clearDraft,
  };
}

function BuildingFormContent({
  isModal = false,
  onCancel,
  onSuccess,
  selection,
}: BuildingFormProps & { selection?: CreationPlanSelection }) {
  const { ref, ...state } = useBuildingForm({
    immediate: isModal,
    onSuccess,
    creationPlan: selection?.consent,
  });
  const setupDraft = useBuildingDraftSelection(state, selection);
  const creationSelection = setupDraft.selection;
  if (!state.isFormReady) return <div ref={ref} className="min-h-[200px]" />;
  const disabled = state.isSubmitting || !state.currentOrganization || !state.canCreate;
  if (isModal) {
    return (
      <BuildingFormModal
        state={state}
        selection={creationSelection}
        disabled={disabled}
        onCancel={() => {
          setupDraft.clearDraft();
          onCancel?.();
        }}
      />
    );
  }
  return (
    <BuildingFormCard
      state={state}
      selection={creationSelection}
      disabled={disabled}
      onCancel={onCancel}
    />
  );
}

function OwnerBuildingForm({ orgId, ...props }: BuildingFormProps & { orgId: string }) {
  const selection = useBuildingCreationPlan(orgId);
  return <BuildingFormContent {...props} selection={selection} />;
}

function BuildingFormStatus({
  loading,
  retry,
  isModal,
  onCancel,
}: BuildingFormProps & { loading: boolean; retry: () => void }) {
  const { t } = useLanguage();
  const content = loading ? (
    <p role="status" className="py-4 text-sm text-muted-foreground">
      {t.common.loading}
    </p>
  ) : (
    <div role="alert" className="space-y-3 py-4 text-sm">
      <p>{t.billing.shell.statusUnknownBody}</p>
      <Button type="button" variant="outline" onClick={retry}>
        {t.common.retry}
      </Button>
    </div>
  );
  return isModal ? (
    <BuildingCreationDialogShell>
      {content}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t.billing.addDialog.cancel}
        </Button>
      </DialogFooter>
    </BuildingCreationDialogShell>
  ) : (
    <div className="p-6">{content}</div>
  );
}

function buildingContextPending(billing: BillingStatusContextValue, orgId: string): boolean {
  return billing.isLoading || billing.orgId !== orgId || billing.context?.org_id !== orgId;
}

const BuildingForm = (props: BuildingFormProps) => {
  const permissions = usePermissions();
  const billing = useBillingStatus();
  const { currentOrganization } = useOrganizationContext();
  const orgId = currentOrganization?.id;
  if (!permissions.isOrgOwner || !orgId) {
    return <BuildingFormContent key={orgId ?? "none"} {...props} />;
  }
  if (buildingContextPending(billing, orgId)) {
    return (
      <BuildingFormStatus
        {...props}
        loading={billing.isLoading}
        retry={() => void billing.refetch()}
      />
    );
  }
  return hasLegacyBillingAccess(billing) ? (
    <BuildingFormContent key={orgId} {...props} />
  ) : (
    <OwnerBuildingForm key={orgId} orgId={orgId} {...props} />
  );
};

export default BuildingForm;
